import {
  init,
  track as plausibleTrack,
  type PlausibleRequestPayload,
} from '@plausible-analytics/tracker';

import { resolveAnalyticsTarget, type AnalyticsTarget } from './config';
import { findPii, sanitiseUrl } from './pii';
import {
  ANALYTICS_EVENTS,
  type AnalyticsEventKey,
  type AnalyticsEventProps,
} from './events';

export { resolveAnalyticsTarget, type AnalyticsTarget } from './config';
export { findPii, sanitiseUrl } from './pii';
export * from './events';

/** Whether `init` has run and succeeded. `track` is a no-op until it has. */
let started = false;

/** Test seam: what the guard did, so a spec can assert on a dropped event. */
export function __resetAnalyticsForTests() {
  started = false;
}

/**
 * The last line of defence, run by the tracker on every outgoing event —
 * including any that reach `window.plausible` without going through `track`.
 *
 * Returning null drops the event. Dropping rather than quietly scrubbing:
 * a missing event gets noticed and fixed, a silently emptied one does not.
 */
export function guardPayload(
  payload: PlausibleRequestPayload,
): PlausibleRequestPayload | null {
  const offenders = findPii(payload.p);
  if (offenders.length) {
    if (import.meta.env.DEV) {
      console.error(
        `[analytics] dropped "${payload.n}" — identifying properties: ${offenders.join(', ')}`,
      );
    }
    return null;
  }

  return {
    ...payload,
    u: sanitiseUrl(payload.u),
    // Our own URLs reach the referrer on in-app navigation, carrying the same
    // invite code the address bar does.
    r: payload.r ? sanitiseUrl(payload.r) || null : payload.r,
  };
}

/**
 * Starts Plausible, or explains why it did not (GIFTWEB-33).
 *
 * Before this, `main.tsx` called `init({ domain: 'gift.miawealth.co.uk' })`
 * unconditionally. The only thing keeping dev traffic out of the production
 * numbers was that the tracker ignores localhost — and `dev.gift.miawealth.co.uk`
 * is not localhost, so every dev deploy was reporting into the live site under
 * the live site's name.
 *
 * Returns the decision so a caller can log or assert on it; it never throws,
 * because analytics failing to start must not take the page down with it.
 */
export function initAnalytics(): AnalyticsTarget {
  const target = resolveAnalyticsTarget({
    domain: import.meta.env.VITE_PLAUSIBLE_DOMAIN,
    hostname: typeof window === 'undefined' ? '' : window.location.hostname,
  });

  if (!target.enabled) {
    if (import.meta.env.DEV) {
      console.info(`[analytics] not started — ${target.reason}`);
    }
    return target;
  }

  try {
    init({ domain: target.domain, transformRequest: guardPayload });
    started = true;
  } catch (error) {
    console.error('[analytics] failed to start', error);
    return { enabled: false, reason: 'tracker threw on init' };
  }

  return target;
}

/**
 * Records one funnel event.
 *
 * The overloads keep events that take no properties from being handed any:
 * `AnalyticsEventProps[K]` is `never` for those, so a second argument will not
 * type-check. Unknown event keys cannot be spelled at all.
 */
export function track<K extends AnalyticsEventKey>(
  event: AnalyticsEventProps[K] extends never ? K : never,
): void;
export function track<K extends AnalyticsEventKey>(
  event: K,
  props: AnalyticsEventProps[K],
): void;
export function track(event: AnalyticsEventKey, props?: object): void {
  if (!started) return;

  try {
    plausibleTrack(ANALYTICS_EVENTS[event], {
      props: props as Record<string, string> | undefined,
    });
  } catch (error) {
    // A failed measurement must never break the journey being measured.
    if (import.meta.env.DEV) {
      console.error(`[analytics] "${event}" failed`, error);
    }
  }
}
